<template>
  <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 py-8 px-2 md:px-0">

    <!-- LEFT CONTENT -->
    <div class="order-1 md:col-span-8 overflow-y-auto md:h-[160vh] pr-2">

      <!-- Loading -->
      <div v-if="loading" class="bg-white p-10 rounded shadow text-center text-gray-500">
        Loading your artist profile...
      </div>

      <!-- Not Logged In -->
      <div v-else-if="!isLoggedIn" class="bg-white p-10 rounded shadow text-center space-y-4">
        <h2 class="text-2xl font-semibold text-red-600">Please log in first</h2>
        <p class="text-gray-600">You need to be logged in to create an artist profile.</p>
        <router-link
          to="/login"
          class="inline-block bg-[#BF3131] text-white px-6 py-2 rounded-md font-semibold"
        >
          Login
        </router-link>
      </div>

      <!-- Logged In but No Artist -->
      <div v-else-if="!hasArtist && !showArtistForm"
        class="bg-white p-10 rounded shadow text-center space-y-4">
        <h2 class="text-2xl font-semibold text-red-600">
          You have not posted anything yet
        </h2>
        <p class="text-gray-600">
          Please create your artist profile first.
        </p>
        <button
          @click="showArtistForm = true"
          class="bg-[#BF3131] text-white px-6 py-2 rounded-md font-semibold"
        >
          Create Artist Profile
        </button>
      </div>

      <!-- INLINE ARTIST CREATE FORM -->
      <ArtistCreateForm
        v-else-if="showArtistForm"
        @created="onArtistCreated"
        @cancel="showArtistForm = false"
        @already-exists="onArtistAlreadyExists"
      />

      <!-- ARTIST PROFILE -->
      <div v-else class="bg-white p-6 rounded shadow h-auto space-y-10 gsap-artist-detail">

        <!-- Top Section -->
        <div class="flex flex-col md:flex-row md:items-start md:space-x-8 space-y-6 md:space-y-0 gsap-header">
          <div class="md:w-2/3 space-y-2 mt-6 text-left">
            <h1 class="text-3xl font-bold text-red-600">{{ artist.name }}</h1>
            <h2 class="text-lg text-gray-500">{{ artist.profession }}</h2>
            <p v-if="artist.quote" class="text-md text-gray-600 italic mt-2 text-center md:text-left">"{{ artist.quote }}"</p>
            <p v-if="artist.dob" class="text-sm text-gray-400 mt-1">Born: {{ new Date(artist.dob).toLocaleDateString() }}</p>
            <div class="w-1/2 h-[2px] bg-gray-300 mt-2"></div>


          </div>

          <div class="md:w-1/3 flex justify-center md:justify-end">
            <img
              :src="artist.image"
              :alt="artist.name"
              class="w-full sm:w-[240px] h-[180px] object-cover rounded-md shadow-md gsap-image"
            />
          </div>
        </div>

        <!-- Description -->
        <div class="gsap-desc">
          <p class="text-gray-600 leading-relaxed text-justify indent-8">
            {{ artist.description }}
          </p>
        </div>

        <!-- Achievements -->
        <div>
          <h3 class="text-lg font-semibold text-[#D84040] mb-2">Achievements</h3>
          <ul class="list-disc list-inside space-y-2 text-gray-600">
            <li v-for="(a, i) in formatAchievements(artist.achievements)" :key="i">
              {{ a }}
            </li>
          </ul>
        </div>

        <!-- Gallery -->
        <section class="gsap-gallery">
          <h2 class="text-2xl font-semibold text-gray-800 mb-4 mt-8">Gallery</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <img
              v-for="(img, index) in artist.galleryImages"
              :key="index"
              :src="img"
              class="w-full h-[220px] object-cover rounded shadow"
            />
          </div>
        </section>

        <!-- Social Links (Moved) -->
        <div v-if="artist.links && artist.links.length" class="mt-8 pt-6 border-t border-gray-100">
          <h3 class="text-lg font-semibold text-[#D84040] mb-4">Connect</h3>
          <div class="flex flex-wrap gap-3">
            <a 
              v-for="(link, index) in artist.links" 
              :key="index" 
              :href="ensureUrl(link.link_url)" 
              target="_blank" 
              rel="noopener noreferrer"
              class="inline-flex items-center px-5 py-2 bg-white text-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all border border-gray-200 group"
            >
              <span class="font-medium">{{ link.link_type }}</span>
              <span class="ml-2 text-[#D84040] group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>
        </div>

      </div>
    </div>

    <!-- RIGHT SIDEBAR -->
    <div class="order-2 md:col-span-4">
      <RightSidebar />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import RightSidebar from "../components/RightsideBar.vue";
import ArtistCreateForm from "../components/ArtistCreateForm.vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const API_BASE_URL = "https://sanskritirdapoon.in/api";
const FILE_BASE_URL = "https://sanskritirdapoon.in";

// STATES
const loading = ref(true);
const hasArtist = ref(true);
const isLoggedIn = ref(false);
const showArtistForm = ref(false);

// ARTIST DATA
const artist = ref({
  name: "",
  profession: "",
  dob: "",
  quote: "",
  description: "",
  achievements: "",
  image: `${FILE_BASE_URL}/images/default.png`,
  galleryImages: [],
});

// ENSURE URL
function ensureUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

// FORMAT ACHIEVEMENTS
function formatAchievements(text) {
  if (!text) return [];
  return text.split(/[\n,]+/).map(a => a.trim()).filter(Boolean);
}

// FETCH ARTIST
async function fetchArtist() {
  loading.value = true;

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  isLoggedIn.value = !!(user && token);

  if (!user?.id) {
    hasArtist.value = false;
    loading.value = false;
    return;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/artists/by-user/${user.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 404) {
      hasArtist.value = false;
      return;
    }

    if (!res.ok) {
      hasArtist.value = false;
      return;
    }

    const data = await res.json();

    artist.value = {
      ...data,
      image: data.image
        ? (data.image.startsWith('data:') ? data.image : `${FILE_BASE_URL}${data.image}`)
        : `${FILE_BASE_URL}/images/default.png`,
      galleryImages: data.galleryImages?.map(i => `${FILE_BASE_URL}${i}`) || [],
    };

    hasArtist.value = true; // User has an artist profile
    await nextTick();
    animate();
  } catch (e) {
    hasArtist.value = false;
  } finally {
    loading.value = false;
  }
}

// AFTER CREATE
function onArtistCreated() {
  showArtistForm.value = false;
  fetchArtist();
}

// HANDLE ALREADY EXISTS
function onArtistAlreadyExists() {
  showArtistForm.value = false;
  fetchArtist(); // Refresh to show existing artist profile
}

// GSAP
function animate() {
  ScrollTrigger.getAll().forEach(t => t.kill());

  gsap.from(".gsap-artist-detail", { opacity: 0, y: 30, duration: 0.6 });
  gsap.from(".gsap-image", { opacity: 0, scale: 0.96, duration: 0.7 });
  gsap.from(".gsap-gallery img", {
    scrollTrigger: { trigger: ".gsap-gallery", start: "top 85%" },
    opacity: 0,
    y: 30,
    stagger: 0.1,
  });
}

onMounted(fetchArtist);
</script>
